import { PosModel } from '@/types'

/**
 * The customer-facing display is a second window of this app at `/customer-display`, usually dragged onto a screen
 * facing the customer. It renders only what the till broadcasts on this channel (same origin, same browser): no
 * session, no sync, no device database, so it can never act as a second till.
 */
export const CUSTOMER_DISPLAY_PATH = '/customer-display'
const CHANNEL = 'iws-pos:customer-display'

export interface DisplayLine {
  id: string
  name: string
  quantity: number
  price: number
  net: number
}

export type DisplayMessage =
  | { type: 'hello' }
  | { type: 'cart'; shop: string; currency: string; lines: DisplayLine[]; totals: PosModel.Totals; customer?: string }
  | { type: 'paid'; shop: string; currency: string; total: number; change: number }

export const openDisplayChannel = () => (typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL))

export const openCustomerDisplay = () =>
  window.open(CUSTOMER_DISPLAY_PATH, 'iws-customer-display', 'popup,width=1024,height=768')

let till: BroadcastChannel | null = null
let last: DisplayMessage | null = null
const listeners = new Set<(m: DisplayMessage) => void>()

/** The last state sent to the display, and changes after it (the companion's Cart view mirrors it). */
export const lastDisplayMessage = () => last
export const onDisplayMessage = (fn: (m: DisplayMessage) => void) => {
  listeners.add(fn)
  return () => void listeners.delete(fn)
}

/** Till side: sends a state to the display and remembers it for a display that opens later. */
export const postToDisplay = (message: DisplayMessage) => {
  // The cart empties right after a sale; keep "Thank you" and the change up until the next item.
  if (message.type === 'cart' && !message.lines.length && last?.type === 'paid') return
  if (!till) {
    till = openDisplayChannel()
    if (till) till.onmessage = (e: MessageEvent<DisplayMessage>) => e.data?.type === 'hello' && last && till?.postMessage(last)
  }
  last = message
  till?.postMessage(message)
  listeners.forEach((fn) => fn(message))
}
